import React from "react";
import { Link } from 'react-router-dom';
import { useEffect, useState } from "react";
import { notification } from 'antd';
import moment from "moment-timezone";
import { Helmet } from "react-helmet";
import { useEffectOnce } from "react-use";
import classNames from "classnames";
import { deleteChat, postUserMessage } from "../../../api/user";
import { useUserStore } from "../../../store/user_store";
import { useChatStore } from "../../../store/chat_store";

const openNotificationWithIcon = (type, info) => {
    notification[type]({
        message: info,
    });
};

const ChatListPage = () => {
    const user = useUserStore().user;
    const { chats, selectedChat, fetchChats, setSelectedChat, messages, fetchMessages } = useChatStore();
    const [innerWidth, setInnerWidth] = useState(window.innerWidth);

    const partner = selectedChat?.partner?.id == user?.id ? selectedChat?.user : selectedChat?.partner;

    const removeChat = async (id) => {
        const remove = await deleteChat(id);
        if (remove != null) {
            openNotificationWithIcon('success', 'Чат успешно удалён!');
            fetchChats();
        } else {
            openNotificationWithIcon('error', 'Что-то пошло не так!');
        }
    }

    useEffect(() => {
        const resizeListener = async () => {
            setInnerWidth(window.innerWidth);
        };
        window.addEventListener('resize', resizeListener);
        return () => {
            window.removeEventListener('resize', resizeListener);
        }
    }, []);

    const postMessage = async (message) => {
        if (message != "" && message != null) {
            const sendMessage = await postUserMessage({ 'user_id': partner?.id, 'message': message });
            if (sendMessage != null) {
                fetchMessages(partner.id);
            }
        } else {
            openNotificationWithIcon('error', 'Заполните поле для сообщения!');
        }
    }

    useEffect(() => {
        if (selectedChat) {
            fetchMessages(partner?.id);
        }
    }, [selectedChat]);

    useEffectOnce(() => {
        moment.locale('ru')
        fetchChats()
        return () => {
            setSelectedChat(null);
        }
    });

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
            <Helmet>
                <title>Сообщения</title>
            </Helmet>

            <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Сообщения</h1>

            <Contents
                chats={chats}
                user={user}
                messages={messages}
                selectedChat={selectedChat}
                onSelectedChatChange={(chat) => {
                    setSelectedChat(null);
                    setTimeout(() => {
                        setSelectedChat(chat);
                    }, 100);
                }}
                onSendMessage={postMessage}
            />
        </div>
    );
}

function Contents({ chats, user, messages, selectedChat, onSelectedChatChange, onSendMessage }) {
    const [message, setMessage] = useState('');
    const scrollRef = React.useRef();

    const partner = selectedChat?.partner?.id == user?.id ? selectedChat?.user : selectedChat?.partner;

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    return (
        <div className="h-[650px] w-full bg-slate-50 border border-slate-100 rounded-3xl flex flex-row gap-4 p-4 shadow-sm">
            {/* Sidebar (Chats List) */}
            <div className="w-[35%] flex flex-col gap-2 overflow-y-auto pr-1">
                {chats?.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-slate-400 text-sm font-semibold">
                        Нет активных чатов
                    </div>
                ) : (
                    chats?.map((item, i) => (
                        <ChatListItem
                            key={i}
                            item={item}
                            isSelected={selectedChat?.id == item.id}
                            onClick={() => onSelectedChatChange(item)}
                            userId={user?.id}
                        />
                    ))
                )}
            </div>

            {/* Chat Messages Area */}
            <div className="w-[65%] flex flex-col bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-sm">
                {selectedChat ? (
                    <div className="flex flex-col h-full">
                        {/* Chat Window Header */}
                        <div className="flex items-center gap-3 p-4 border-b border-slate-100 bg-slate-50/50">
                            <span className="w-10 h-10 rounded-full overflow-hidden bg-slate-200 flex-shrink-0 shadow-sm">
                                {partner?.image ? (
                                    <img src={partner.image} alt={partner.name} className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-slate-500 font-bold text-sm uppercase">
                                        {partner?.name?.charAt(0)}
                                    </div>
                                )}
                            </span>
                            <div className="flex-1 min-w-0">
                                <h3 className="text-sm font-bold text-slate-800 truncate">{partner?.name}</h3>
                                <span className="text-[10px] font-semibold text-emerald-500 flex items-center gap-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                                    в сети
                                </span>
                            </div>
                        </div>

                        {/* Messages Container */}
                        <div className="flex-1 overflow-y-auto p-4 bg-slate-50/30">
                            <div className="flex flex-col gap-3">
                                {messages?.map((item, index) => {
                                    const isMe = item.sender?.id == user?.id;
                                    return (
                                        <div
                                            key={index}
                                            className={classNames(
                                                "flex w-full",
                                                { 'justify-end': isMe, 'justify-start': !isMe }
                                            )}
                                        >
                                            <div
                                                className={classNames(
                                                    "px-4 py-2.5 rounded-2xl max-w-[70%] text-sm leading-relaxed shadow-sm font-medium",
                                                    { 
                                                        'bg-blue-600 text-white rounded-tr-none': isMe, 
                                                        'bg-white border border-slate-150 text-slate-700 rounded-tl-none': !isMe 
                                                    }
                                                )}
                                            >
                                                <p className="break-words">{item.message}</p>
                                                <span className={classNames(
                                                    "block text-[9px] text-right mt-1 font-semibold",
                                                    { 'text-blue-100': isMe, 'text-slate-400': !isMe }
                                                )}>
                                                    {moment(item.created_at).format('HH:mm')}
                                                </span>
                                            </div>
                                        </div>
                                    )
                                })}
                                <div ref={scrollRef} />
                            </div>
                        </div>

                        {/* Message Input Bottom Panel */}
                        <div className="p-3 border-t border-slate-100 bg-slate-50/50">
                            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl p-1 px-2 shadow-sm focus-within:ring-2 focus-within:ring-blue-600 focus-within:border-transparent">
                                <input
                                    className="w-full h-10 border-none focus:outline-none px-2 bg-transparent text-sm text-slate-700 font-medium placeholder-slate-400"
                                    placeholder="Напишите сообщение..."
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    onKeyPress={async (e) => {
                                        if (e.key === 'Enter') {
                                            await onSendMessage(message);
                                            setMessage('');
                                        }
                                    }}
                                />
                                <button
                                    className="h-9 w-9 rounded-lg bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center transition"
                                    onClick={async () => {
                                        await onSendMessage(message);
                                        setMessage('');
                                    }}
                                    aria-label="Send message"
                                >
                                    <svg className="w-4 h-4 transform rotate-90 text-white mr-0.5" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center h-full text-slate-400 space-y-2">
                        <svg className="w-12 h-12 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                        <span className="text-sm font-semibold text-slate-400">Выберите диалог, чтобы начать общение</span>
                    </div>
                )}
            </div>
        </div>
    );
}

function ChatListItem({ item, isSelected = false, userId, onClick }) {
    const partner = item.partner?.id === userId ? item.user : item.partner;
    const image = partner?.image;

    return (
        <div
            className={classNames(
                "flex flex-row items-center gap-3 rounded-2xl p-3 cursor-pointer transition-all duration-200 border",
                { 
                    'bg-blue-600 border-blue-600 text-white shadow-md shadow-blue-100': isSelected, 
                    'bg-white border-slate-100 hover:bg-slate-50/80 text-slate-700': !isSelected 
                }
            )}
            onClick={onClick}
        >
            {/* Avatar */}
            <span className="w-11 h-11 rounded-full overflow-hidden bg-slate-200 flex-shrink-0 shadow-sm flex items-center justify-center">
                {image ? (
                    <img src={image} alt="avatar" className="w-full h-full object-cover" />
                ) : (
                    <span className={classNames(
                        "font-bold text-sm uppercase",
                        { 'text-white': isSelected, 'text-slate-500': !isSelected }
                    )}>
                        {partner?.name?.charAt(0)}
                    </span>
                )}
            </span>

            {/* Title & Preview */}
            <div className="flex-1 min-w-0 flex flex-col justify-center">
                <div className="flex items-center justify-between">
                    <span className={classNames(
                        "text-sm font-bold truncate",
                        { 'text-white': isSelected, 'text-slate-800': !isSelected }
                    )}>
                        {partner?.name}
                    </span>
                    <span className={classNames(
                        "text-[9px] font-semibold",
                        { 'text-blue-100': isSelected, 'text-slate-400': !isSelected }
                    )}>
                        {item.lastMessage ? moment(item.lastMessage.created_at).format('HH:mm') : ''}
                    </span>
                </div>
                <div className={classNames(
                    "text-xs truncate mt-0.5 font-medium",
                    { 'text-blue-100': isSelected, 'text-slate-500': !isSelected }
                )}>
                    {item.lastMessage?.message || 'Нет сообщений'}
                </div>
            </div>
        </div>
    );
}

export default ChatListPage;